import { buildPublicJsonHeaders } from "./publicCatalogueHelpers";
import { resolveApiV1Root } from "./catalogueCommerceClient";
import type { CatalogueVisitorProfile } from "./catalogueVisitorSession";

type ApiEnvelope = {
  success?: boolean;
  message?: string;
  data?: unknown;
  status?: number;
};

async function parseEnvelope(res: Response, text: string): Promise<ApiEnvelope> {
  let parsed: ApiEnvelope | null = null;
  try {
    parsed = text ? (JSON.parse(text) as ApiEnvelope) : null;
  } catch {
    return {
      success: false,
      message: res.ok
        ? "Unexpected response from server."
        : `Request failed (HTTP ${res.status}).`,
    };
  }
  if (parsed && typeof parsed === "object") {
    if (parsed.success === false && !parsed.message) {
      parsed.message = `Request failed (HTTP ${res.status}).`;
    }
    return parsed;
  }
  return {
    success: res.ok,
    message: res.ok ? undefined : `Request failed (HTTP ${res.status}).`,
  };
}

async function requestJson(path: string, init: RequestInit = {}): Promise<ApiEnvelope> {
  const root = resolveApiV1Root();
  if (!root) {
    return {
      success: false,
      message: "API URL is not configured (REACT_APP_SERVERURL).",
    };
  }
  const url = `${root}${path.startsWith("/") ? path : `/${path}`}`;
  const headers: HeadersInit = {
    ...(buildPublicJsonHeaders() as Record<string, string>),
    ...(init.headers as Record<string, string> | undefined),
  };
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (e: unknown) {
    const detail = e instanceof Error ? e.message : "";
    const base =
      "Could not reach the API. Check that the backend is running, then restart it after a fresh build so routes like /catalogue-visitor exist.";
    return {
      success: false,
      message: detail ? `${base} (${detail})` : base,
    };
  }
  const text = await res.text();
  return parseEnvelope(res, text);
}

export async function registerCatalogueVisitor(body: {
  name: string;
  phone: string;
  email: string;
  /** Public catalogue id — backend resolves studio org and optional party link. */
  catalogueId?: string;
  /** Optional GSTIN when supplied (15-char Indian format). */
  gst?: string;
  /** Optional business name; defaults to person name for the linked party organisation. */
  organizationName?: string;
}): Promise<ApiEnvelope> {
  return requestJson("/catalogue-visitor/register", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function loginCatalogueVisitor(body: { phone: string }): Promise<ApiEnvelope> {
  return requestJson("/catalogue-visitor/login", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type VisitorAuthPayload = {
  token: string;
  profile: CatalogueVisitorProfile | null;
};

/** Login / OTP payloads: `{ token, profile }` or `{ visitorToken, visitor }`. */
export function parseVisitorAuthPayload(data: unknown): VisitorAuthPayload | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const tokenRaw =
    d.token != null ? String(d.token) : d.visitorToken != null ? String(d.visitorToken) : "";
  const token = tokenRaw.trim();
  if (!token) return null;
  const rawProfile = d.profile && typeof d.profile === "object" ? d.profile : d.visitor;
  let profile: CatalogueVisitorProfile | null = null;
  if (rawProfile && typeof rawProfile === "object") {
    const p = rawProfile as Record<string, unknown>;
    const visitorId = p.visitorId != null ? String(p.visitorId) : "";
    if (visitorId) {
      profile = {
        visitorId,
        name: p.name != null ? String(p.name) : "",
        phone: p.phone != null ? String(p.phone) : "",
        email: p.email != null ? String(p.email) : undefined,
      };
    }
  }
  return { token, profile };
}

/** After invite OTP has proven the phone, restore the visitor account if it exists. */
export async function attachVisitorForVerifiedPhone(
  phone: string
): Promise<VisitorAuthPayload | null> {
  const json = await loginCatalogueVisitor({ phone });
  if (json?.success && json.data) {
    return parseVisitorAuthPayload(json.data);
  }
  return null;
}

/**
 * Restore or create the invitee visitor from a proven catalogue viewer session.
 * The backend uses the share-link phone on that session — not a client-supplied number.
 */
export async function attachVisitorFromCatalogueSession(
  catalogueId: string,
  sessionToken: string
): Promise<VisitorAuthPayload | null> {
  const cid = String(catalogueId || "").trim();
  const session = String(sessionToken || "").trim();
  if (!cid || !session) return null;
  const json = await requestJson("/catalogue-visitor/attach-from-session", {
    method: "POST",
    headers: { "X-HKP-Catalogue-Session": session },
    body: JSON.stringify({ catalogueId: cid }),
  });
  if (json?.success && json.data) {
    return parseVisitorAuthPayload(json.data);
  }
  return null;
}

export async function fetchCatalogueVisitorMe(): Promise<ApiEnvelope> {
  return requestJson("/catalogue-visitor/me", { method: "GET" });
}

export async function fetchVisitorShelves(): Promise<ApiEnvelope> {
  return requestJson("/catalogue-visitor/shelf", { method: "GET" });
}

export async function upsertVisitorShelfShortlist(
  catalogueId: string,
  body: Record<string, unknown>
): Promise<ApiEnvelope> {
  const id = encodeURIComponent(catalogueId);
  return requestJson(`/catalogue-visitor/shelf/catalogue/${id}/shortlist`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function upsertVisitorShelfCartLine(
  catalogueId: string,
  body: Record<string, unknown>
): Promise<ApiEnvelope> {
  const id = encodeURIComponent(catalogueId);
  return requestJson(`/catalogue-visitor/shelf/catalogue/${id}/cart`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function removeVisitorShelfCartLine(
  catalogueId: string,
  productKey: string
): Promise<ApiEnvelope> {
  const cid = encodeURIComponent(catalogueId);
  const pk = encodeURIComponent(productKey);
  return requestJson(`/catalogue-visitor/shelf/catalogue/${cid}/cart/${pk}`, {
    method: "DELETE",
  });
}

export async function removeVisitorShelfShortlistLine(
  catalogueId: string,
  productKey: string
): Promise<ApiEnvelope> {
  const cid = encodeURIComponent(catalogueId);
  const pk = encodeURIComponent(productKey);
  return requestJson(`/catalogue-visitor/shelf/catalogue/${cid}/shortlist/${pk}`, {
    method: "DELETE",
  });
}

/** Clears draft shelf lines after a successful submit to the studio (visitor JWT). */
export async function clearVisitorShelfDraft(
  catalogueId: string,
  body: { clearCart: boolean; clearShortlist: boolean }
): Promise<ApiEnvelope> {
  const cid = encodeURIComponent(catalogueId);
  return requestJson(`/catalogue-visitor/shelf/catalogue/${cid}/clear`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
